import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './hooks/useAuth';
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import Planning from './pages/Planning';
import Actual from './pages/Actual';
import SettingsModal from './components/SettingsModal';
import LoginScreen from './components/LoginScreen';
import { applyTheme, loadThemePref, saveThemePref } from './lib/theme';
import { apiGet, apiSend } from './lib/api';

const pathToPage = p => (p === '/planning' ? 'planning' : p === '/actual' ? 'actual' : 'dashboard');

function App() {
  const { login, logout, isLoggedIn } = useAuth();

  const [page, setPage] = useState(pathToPage(window.location.pathname));
  const [data, setData] = useState(null);
  const [savedPlan, setSavedPlan] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [members, setMembers] = useState({});
  const [fcPrefs, setFcPrefs] = useState({}); // saved forecast-card year selections
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [theme, setTheme] = useState(() => ({ accent: 'sunset', mode: 'dark', ...loadThemePref() }));
  const themeRef = useRef(theme);
  themeRef.current = theme;

  // Apply theme to the DOM whenever it changes (no persistence here).
  useEffect(() => { applyTheme(theme); }, [theme]);

  // Persist ONLY on an explicit user change (Settings). Never on login/load —
  // that race used to write the previous user's theme onto the new account.
  const changeTheme = useCallback(updater => {
    const next = typeof updater === 'function' ? updater(themeRef.current) : updater;
    setTheme(next);
    saveThemePref(next);
    if (isLoggedIn) apiSend('/theme', { theme: next }).catch(() => {});
  }, [isLoggedIn]);

  // single data fetch after login (shared across all tabs)
  useEffect(() => {
    if (!isLoggedIn) return;
    setLoading(true);
    (async () => {
      // verify the session token first — bad/stale token must never render the app
      const vr = await apiGet('/verify');
      if (vr.status === 401) { logout(); setLoading(false); return; }
      if (!vr.ok) { setLoading(false); return; }
      const [d, p, m, pr, th, fp] = await Promise.all([
        apiGet('/dashboard').then(r => r.json()),
        apiGet('/plan').then(r => r.json()),
        apiGet('/members').then(r => r.json()).catch(() => ({ members: {} })),
        apiGet('/profiles').then(r => r.json()).catch(() => ({ profiles: [] })),
        apiGet('/theme').then(r => r.json()).catch(() => ({ theme: null })),
        apiGet('/fcprefs').then(r => r.json()).catch(() => ({ prefs: null })),
      ]);
      setData(d);
      if (p.exists) { setSavedPlan(p.plan); setSavedAt(p.updated_at); }
      setMembers(m.members || {});
      setProfile((pr.profiles || []).find(x => x.id === d.user_id) || null);
      setFcPrefs(fp.prefs || {});
      // Always adopt this user's server theme (or default) — never inherit the
      // previously logged-in user's cached theme. setTheme (not changeTheme) = no save.
      setTheme(th.theme ? { accent: 'sunset', mode: 'dark', ...th.theme } : { accent: 'sunset', mode: 'dark' });
      setLoading(false);
    })().catch(() => setLoading(false));
  }, [isLoggedIn, logout]);


  // Live refresh: re-pull /dashboard every 10s so US stocks (and the INR figures
  // derived from the live FX rate) stay current. Server re-prices USD holdings.
  useEffect(() => {
    if (!isLoggedIn) return;
    const id = setInterval(() => {
      apiGet('/dashboard')
        .then(r => r.json())
        .then(d => { if (d && !d.error && !d.empty) setData(d); })
        .catch(() => {});
    }, 10000);
    return () => clearInterval(id);
  }, [isLoggedIn]);

  const navigate = useCallback(pg => {
    setPage(pg);
    const path = pg === 'dashboard' ? '/' : `/${pg}`;
    window.history.pushState({}, '', path);
  }, []);

  useEffect(() => {
    const onPop = () => setPage(pathToPage(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Password login: posts to /auth/login (password in body, never URL) → session token.
  const attemptLogin = useCallback(async (pwd, expectedId) => {
    try {
      const r = await fetch('/auth/login', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: expectedId, password: pwd }),
      });
      if (r.status === 401) return 'Incorrect password';
      if (r.status === 429 || r.status === 503) return 'Too many attempts — wait a minute';
      if (!r.ok) return 'Server error — try again';
      const { user, token } = await r.json();
      try { localStorage.setItem('lastProfile', expectedId || user); } catch { /* ignore */ }
      login(token);
      return null;
    } catch {
      return 'Network error';
    }
  }, [login]);

  // Passkey login: token already obtained via WebAuthn; just store it + remember profile.
  const passkeyLogin = useCallback((token, user) => {
    try { localStorage.setItem('lastProfile', user); } catch { /* ignore */ }
    login(token);
  }, [login]);

  const saveFcPrefs = useCallback((patch) => {
    setFcPrefs(prev => {
      const next = { ...prev, ...patch };
      apiSend('/fcprefs', { prefs: next }).catch(() => {});
      return next;
    });
  }, []);

  const saveMembers = useCallback(async (next) => {
    setMembers(next);
    try { await apiSend('/members', { members: next }); } catch { /* ignore */ }
  }, []);

  if (!isLoggedIn) {
    return <LoginScreen attemptLogin={attemptLogin} onPasskeyLogin={passkeyLogin} />;
  }

  return (
    <Layout onLogout={logout} activePage={page} onNavigate={navigate} onOpenSettings={() => setSettingsOpen(true)} userId={data?.user_id} members={members}>
      {loading || !data ? (
        <div className="flex items-center justify-center" style={{ height: '70vh', color: 'var(--text-dim)', fontSize: 16 }}>Loading portfolio…</div>
      ) : page === 'planning' ? (
        <Planning data={data} savedPlan={savedPlan} savedAt={savedAt} members={members}
          fcPrefs={fcPrefs} saveFcPrefs={saveFcPrefs}
          onSaved={(p, at) => { setSavedPlan(p); setSavedAt(at); }} />
      ) : page === 'actual' ? (
        <Actual data={data} members={members} savedPlan={savedPlan} fcPrefs={fcPrefs} saveFcPrefs={saveFcPrefs} />
      ) : (
        <Dashboard data={data} savedPlan={savedPlan} savedAt={savedAt} members={members} />
      )}
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)}
        theme={theme} setTheme={changeTheme} entities={(data?.entity_order?.length ? data.entity_order : ['PRANAV', 'RASHU', 'HUF'])} members={members} saveMembers={saveMembers} />
    </Layout>
  );
}

export default App;
